// src/sections/jobFest/JobFestHowItWorks.tsx
import React, { useMemo } from "react";

// Images
import buildingsImg from "../../assets/jobfest/buildings.png";
import candidateImg from "../../assets/jobfest/candidate.png";

type Phase = "companies" | "interviews" | "offers";

// Timeline range
const START_DAY = 8;
const END_DAY = 19;

const getPhaseForDay = (day: number): Phase => {
  if (day >= 8 && day <= 12) return "companies"; // cohort seats open
  if (day >= 13 && day <= 16) return "interviews"; // AI interviews
  return "offers"; // final interviews & offers
};

export const JobFestHowItWorks: React.FC = () => {
  const days = useMemo(
    () =>
      Array.from({ length: END_DAY - START_DAY + 1 }, (_, i) => {
        const day = START_DAY + i;
        return {
          day,
          label: `Dec ${day}`,
          phase: getPhaseForDay(day),
        };
      }),
    []
  );

  // Today marker + progress line (only active for Dec 2025, 8–19)
  const { todayIndex, progress } = useMemo(() => {
    const now = new Date();
    const isDec2025 = now.getFullYear() === 2025 && now.getMonth() === 11; // 11 = Dec

    if (!isDec2025) {
      return { todayIndex: -1, progress: 0 };
    }

    const d = now.getDate();
    if (d < START_DAY) return { todayIndex: -1, progress: 0 };
    if (d > END_DAY) return { todayIndex: -1, progress: 1 };

    const idx = d - START_DAY;
    const totalSpan = END_DAY - START_DAY;
    const ratio = totalSpan === 0 ? 0 : idx / totalSpan;

    return {
      todayIndex: idx,
      progress: Math.max(0, Math.min(1, ratio)),
    };
  }, []);

  const hasToday = todayIndex !== -1;

  return (
    <section className="how-section jobfest-timeline">
      <div className="section-header">
        <p className="section-kicker">How it works</p>
        <h2 className="section-title">One 12-day AI hiring sprint.</h2>
        
      </div>

      {/* Legend row */}
      <div className="jf-legend">
        <div className="jf-legend-item">
          <span className="jf-legend-dot jf-legend-dot--companies" />
          <span>Companies onboarding</span>
        </div>
        <div className="jf-legend-item">
          <span className="jf-legend-dot jf-legend-dot--interviews" />
          <span>AI interviews running</span>
        </div>
        <div className="jf-legend-item">
          <span className="jf-legend-dot jf-legend-dot--offers" />
          <span>Offer days</span>
        </div>
      </div>

      {/* TIMELINE STRIP */}
      <div
        className={
          "jf-timeline-shell" + (hasToday ? "" : " jf-no-today-marker")
        }
        style={{ ["--jf-progress" as any]: progress }}
      >
        {/* Base line + progress overlay */}
        <div className="jf-timeline-line" />
        <div className="jf-timeline-progress" />

        {/* vertical line + avatars (positioned via --jf-progress in CSS) */}
        <div className="jf-today-line" />

        {/* floating company + candidate markers */}
        <div className="jf-marker jf-marker--company">
          <img src={buildingsImg} alt="Companies" />
        </div>
        <div className="jf-marker jf-marker--candidate">
          <img src={candidateImg} alt="Candidate" />
        </div>

        {/* Day circles */}
        <div className="jf-timeline-days">
          {days.map((d, index) => {
            const isToday = index === todayIndex;

            return (
              <div key={d.day} className="jf-day">
                <div
                  className={[
                    "jf-day-circle",
                    `jf-day-circle--${d.phase}`,
                    isToday ? "jf-day-circle--today" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <span className="jf-day-number">{d.day}</span>
                </div>
                <span className="jf-day-label">DEC</span>
              </div>
            );
          })}
        </div>

        {/* Captions under strip, with coloured dots only */}
        <div className="jf-phase-captions">
          <div className="jf-phase-caption jf-phase-caption--left">
            <span className="jf-phase-dot jf-phase-dot--companies" />
            8–12 Dec · Cohort seats open for companies
          </div>
          <div className="jf-phase-caption jf-phase-caption--center">
            <span className="jf-phase-dot jf-phase-dot--interviews" />
            13–16 Dec · Candidates take Babblebots AI Interview
          </div>
          <div className="jf-phase-caption jf-phase-caption--right">
            <span className="jf-phase-dot jf-phase-dot--offers" />
            17–19 Dec · Final interviews &amp; offers
          </div>
        </div>
      </div>
    </section>
  );
};
